import React, { useEffect, useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import useUserContext from '../hooks/use-user-context';
import { createOptionMap } from '../utilities/misc';

import { colors } from '../theme/colors';
// There won't be a circular reference because widgetCaption does not allow embedded entries
// eslint-disable-next-line import/no-cycle
import TextRenderer from './TextRenderer';

const TrustRadiusWidget = styled.div`
  min-height: 350px;
  background-color: #fff !important;
  color: ${colors.black} !important;
  .tr-banner-tagline {
    font-family: 'Poppins','Poppins-Fallback',Verdana,sans-serif !important;
    color: ${colors.black} !important;
  }
  p, .tr-review-byline *, .incentivized {
    color: ${colors.black} !important;
  }
`;

const CapterraWidget = styled.iframe`
  height: 650px;
  width: 100%;
  border: none;
`;

const BugcrowdWidget = styled.div`
  min-height: 350px;
`;

const StorylaneWidget = styled.div`
  position: relative;
  overflow: hidden;
  max-width: 750px;
  margin: 15px auto;
  padding-bottom: 56.25%;
  transform: scale(1);
`;

const StorylaneIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  border: 1px solid rgba(63, 95, 172, 0.35);
  box-shadow: 0px 0px 18px rgba(26, 19, 72, 0.15);
  border-radius: 10px;
`;

const ImpactWidget = styled.div`
  min-height: 60px;
`;

const GoogleMapsWidget = styled.iframe`
  max-width: 100%;
  width: 640px;
  height: 480px;
  border: none;
  margin-top: 1rem;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const Container = styled.div`
  max-width: 100%;
`;

const CaptionWrapper = styled.div`
  padding-top: 0.75rem;
`;

const WidgetItem = ({
  widgetTitle,
  widgetType,
  widgetId,
  widgetCaption,
}) => {
  let widgetSrc = null;
  const bugcrowdWidgetRef = useRef(null);

  if (widgetType === 'TrustRadius') widgetSrc = 'https://www.trustradius.com/trustquotes/trustquotes.js';
  if (widgetType === 'Capterra') widgetSrc = 'https://competitivecomparisons.capterra.com/';
  if (widgetType === 'Bugcrowd') widgetSrc = `https://bugcrowd.com/${widgetId}/external/script`;
  if (widgetType === 'Storylane') widgetSrc = 'https://js.storylane.io/js/v2/storylane.js';

  const data = useStaticQuery(graphql`
    query SkipToMain {
      allContentfulOptionSet(filter: {name: {eq: "globals"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  useEffect(() => {
    let script;

    const handleWidgetScript = () => {
      if (!widgetSrc) return;

      // Check if the script is already present
      if (!document.querySelector(`script[src="${widgetSrc}"]`)) {
        script = document.createElement('script');
        script.src = widgetSrc;
        script.type = 'text/javascript';
        script.async = true;

        if (widgetType === 'Bugcrowd') {
          script.setAttribute('data-bugcrowd-program', `https://bugcrowd.com/${widgetId}/external/report`);
          bugcrowdWidgetRef.current.appendChild(script);
        } else {
          document.body.appendChild(script);
        }
      }
    };

    if (widgetType === 'TrustRadius' || widgetType === 'Bugcrowd' || widgetType === 'Storylane') {
      handleWidgetScript();
    }

    // Cleanup function to remove the script on component unmount
    return () => {
      if (script && widgetType !== 'Bugcrowd') {
        document.body.removeChild(script);
      }
    };
  }, [widgetSrc, widgetType, widgetId]);

  const addUniversalTrackingTag = () => {
    // Ensure the code only runs in the browser
    if (typeof window === 'undefined') return;

    console.log('Universal Tracking Tag loaded');
    const script = document.createElement('script');
    script.src = 'https://utt.impactcdn.com/A5628474-671a-4ec6-99c9-ab008075da6e1.js';
    script.async = true;

    // Append the script to the document head
    document.head.appendChild(script);

    // Initialize the tracking tag using rest parameters
    window.ire = window.ire || function ireHandler(...args) {
      (window.ire.a = window.ire.a || []).push(args);
    };
  };

  return (
    <Container>
      {widgetTitle && <h3>{widgetTitle}</h3>}
      {widgetType === 'TrustRadius' && (
        <TrustRadiusWidget
          className="trustradius-tqw"
          data-trustradius-id={widgetId}
        />
      )}
      {widgetType === 'Capterra' && (
        <CapterraWidget
          title={widgetTitle}
          id={`gdm-cc_${widgetId}`}
          src={widgetSrc + widgetId}
        />
      )}
      {widgetType === 'Bugcrowd' && (
        <BugcrowdWidget ref={bugcrowdWidgetRef} />
      )}
      {widgetType === 'Storylane' && (
        <StorylaneWidget>
          <StorylaneIframe
            onLoad={() => window.dispatchEvent(new Event('resize'))}
            loading="lazy"
            src={`https://app.storylane.io/demo/${widgetId}?embed=inline`}
            name="sl-embed"
            allow="fullscreen"
            allowFullScreen
          />
        </StorylaneWidget>
      )}
      {widgetType === 'Impact' && (
        <ImpactWidget>
          {/* Load the universal tracking tag only for Impact widget */}
          {addUniversalTrackingTag()}
          <impact-embed widget="p/26601/w/websiteReferralWidget">
            <div>{options['globals.hubspot_form_loading_message']}</div>
          </impact-embed>
        </ImpactWidget>
      )}
      {widgetType === 'Google Maps' && (
        <GoogleMapsWidget
          title={widgetTitle}
          src={`https://www.google.com/maps/d/u/0/embed?mid=${widgetId}`}
        />
      )}
      {widgetCaption && (
        <CaptionWrapper>
          <TextRenderer text={widgetCaption} richText />
        </CaptionWrapper>
      )}
    </Container>
  );
};

WidgetItem.propTypes = {
  widgetTitle: PropTypes.string,
  widgetType: PropTypes.string,
  widgetId: PropTypes.string,
  widgetCaption: PropTypes.shape({
    raw: PropTypes.string,
  }),
};

export default WidgetItem;
